
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Card, CardContent, CardActions, Button,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stepper, Step, StepLabel, Alert, Snackbar, CircularProgress,
  Avatar, Divider, IconButton, InputAdornment, Paper, Stack,
  useMediaQuery, useTheme as useMuiTheme, Container,Grid,Checkbox, FormControlLabel
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowBack, PlayCircleFilledWhite } from "@mui/icons-material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import {
    LocalHospital as LocalHospitalIcon,
    FitnessCenter as FitnessCenterIcon,
    CurrencyRupee as CurrencyRupeeIcon,
    AccessTime as AccessTimeIcon,
    BookmarkAdd as BookmarkAddIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    History as HistoryIcon,
    Phone as PhoneIcon,
    Person as PersonIcon,
    LocationOn as LocationOnIcon,
    Event as EventIcon,
    Close as CloseIcon,
    Schedule as ScheduleIcon,
    MedicalServices as MedicalServicesIcon,
    Tag as TagIcon,
    ArrowBack as ArrowBackIcon,
    VerifiedUser as VerifiedUserIcon,
    Star as StarIcon,
    CalendarToday as CalendarTodayIcon,
     Visibility as VisibilityIcon,
  } from "@mui/icons-material";
import axios from 'axios'
// ── Separated My Bookings page/dialog ───────────────────────────────────────
import MyNurPhyBookingsPage from "./MyNursingPhybookings";
import  bg2 from "../Logos/med_background2.png"
import { ChevronLeft, Edit2, Tag, ChevronDown, ChevronUp, Plus, Minus, Trash2, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

// ── Header layout constants — must match Header.jsx exactly ──────────────────
const DRAWER_WIDTH = 220;
const MOBILE_BAR_H = 56;

const API = process.env.REACT_APP_API_BASE_URL;

// ── helpers outside component — avoids stale closure in useCallback ──────────
function getAuthHeader() {
  const token = sessionStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}
function getUserId() {
  return sessionStorage.getItem("userId") || null;
}

// ── Ayuja-exact theme ────────────────────────────────────────────────────────
const ayujaTheme = createTheme({
  palette: {
    mode: "light",
    primary:    { main: "#006D77", light: "#00949f", dark: "#004d55", contrastText: "#fff" },
    secondary:  { main: "#E29578" },
    success:    { main: "#16a34a" },
    warning:    { main: "#d97706" },
    error:      { main: "#dc2626" },
    info:       { main: "#0284c7" },
    background: { default: "#f0f7f8", paper: "#ffffff" },
    text:       { primary: "#0f2b2e", secondary: "#4a6b6f" },
  },
  typography: {
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    h4: { fontWeight: 800, letterSpacing: -0.5 },
    h5: { fontWeight: 800 },
    h6: { fontWeight: 700 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 12px rgba(0,109,119,0.08)",
          border: "1px solid rgba(0,109,119,0.1)",
          transition: "box-shadow 0.25s ease, transform 0.25s ease",
          "&:hover": {
            boxShadow: "0 8px 28px rgba(0,109,119,0.18)",
            transform: "translateY(-3px)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 700, borderRadius: 8 },
        containedPrimary: {
          background: "linear-gradient(135deg, #006D77 0%, #00949f 100%)",
          boxShadow: "0 4px 14px rgba(0,109,119,0.3)",
          "&:hover": { boxShadow: "0 6px 20px rgba(0,109,119,0.42)" },
        },
      },
    },
    MuiTab:       { styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 44 } } },
    MuiChip:      { styleOverrides: { root: { fontWeight: 600 } } },
    MuiTextField: { defaultProps: { size: "small" } },
    MuiStepIcon: {
      styleOverrides: {
        root: { "&.Mui-active": { color: "#006D77" }, "&.Mui-completed": { color: "#16a34a" } },
      },
    },
  },
});
const styles={
    promoInput: { display: "flex", gap: "8px", marginTop: "12px" },
    input: { flex: 1, border: "1px solid #D1D5DB", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", fontFamily: "inherit" },
    button: { padding: "10px 20px", borderRadius: "8px", border: "none", cursor: "pointer", fontSize: "14px", fontWeight: "600" },
    primaryButton: { backgroundColor: "#006D77", color: "#fff" },
    secondaryButton: { backgroundColor: "#fff", color: "#006D77", border: "1px solid #006D77" },
    dangerButton: { backgroundColor: "#EF4444", color: "#fff" },
    promoTag: { display: "inline-flex", alignItems: "center", gap: "8px", backgroundColor: "#D1FAE5", color: "#065F46", padding: "8px 12px", borderRadius: "8px", fontSize: "14px", fontWeight: "600", marginTop: "8px" },
    promoCard: { border: "1px solid #E5E7EB", borderRadius: "12px", padding: "16px", marginBottom: "12px", cursor: "pointer" },
    inputGroup: { marginBottom: "20px" },
}


const TIME_SLOTS = [
  "08:00", "09:00", "10:00", "11:00",
  "12:00", "13:00", "14:00", "15:00",
  "16:00", "17:00", "18:00",
];

const TODAY = new Date().toISOString().split("T")[0];
// const STEPS = ["Your Details", "Schedule", "Review & Confirm"];

function emptyForm() {
  return { patient_name: "", patient_phone: "", patient_address: "", start_date: "",end_date: "", scheduled_time: "", notes: "", promo_code: {} };
}

function StatPill({ icon, label }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 0.7,
      bgcolor: "rgba(12, 129, 133, 0.32)", border: "1px solid rgba(14, 87, 100, 0.81)",
      borderRadius: 20, px: 1.5, py: 0.5,
    }}>
      <Box sx={{ "& svg": { fontSize: 13, display: "block" }, color: "rgba(0,0,0,0.9)" }}>{icon}</Box>
      <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600, color: "rgba(0,0,0,0.95)", whiteSpace: "nowrap" }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function NursingPhysiotherapyPage() {
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [tab,          setTab]          = useState(0);
  const [services,     setServices]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [bookingDlg,   setBookingDlg]   = useState({ open: false, service: null });
  const [myBkgOpen,    setMyBkgOpen]    = useState(false);   // ← controls MyBookingsPage
  const [step,         setStep]         = useState(0);
  const [success,      setSuccess]      = useState(false);
  const [form,         setForm]         = useState(emptyForm());
  const [snack,        setSnack]        = useState({ open: false, msg: "", sev: "success" });
  const [bookingLoading, setBookingLoading] = useState(false);

    // promo
    const [promoCode, setPromoCode] = useState("");
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [promoLoading, setPromoLoading] = useState(false);
    const [showAvailablePromos, setShowAvailablePromos] = useState(false);
    const [availablePromos, setAvailablePromos] = useState([]);
    const [promoMsg, setPromoMsg] = useState("")
    const [promotype, setPromotype] = useState("")
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
  
  const route =  useLocation();
  const {selectedTest} = route.state || {};
  const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  
const [medicalDocs, setMedicalDocs] = useState([]);
const [docPreviewUrl, setDocPreviewUrl] = useState("");
const [docUploading, setDocUploading] = useState(false);
const [docError, setDocError] = useState("");
const [bookingRef, setBookingRef] = useState("");
const [acceptedTerms,setAcceptedTerms]=useState(false)

const [docPreviewIsPdf, setDocPreviewIsPdf] = useState(false);
const MAX_DOC_MB = 5;
const MAX_DOCS = 15;
const ALLOWED_DOC_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const DELIVERY_CHARGES = sessionStorage.getItem("community") !== "Honer Vivantis"
    ? Number(process.env.REACT_APP_DELIVERY_CHARGE || 0) :0;


// Steps are dynamic — physiotherapy bookings get an extra step
const isPhysio = bookingDlg.service?.category === "physiotherapy";
const STEPS = isPhysio
  ? ["Your Details", "Schedule", "Medical Records", "Review & Confirm"]
  : ["Your Details", "Schedule", "Review & Confirm"];

    const justAppliedPromo = React.useRef(false);
  
    // ─── Available promos fetch ────────────────────────────────────────────────
    useEffect(() => {
      const fetchAvailablePromos = async () => {
        try {
          const userId = sessionStorage.getItem("userId");
          const response = await axios.get(
            `${process.env.REACT_APP_APPLY_PROMOTION_API}/available?service_type=nursingAndPhysio&user_id=${userId}`,
            { headers: { "Content-Type": "application/json" } }
          );
          const data = response.data;
          if (data?.success) setAvailablePromos(data.promotions || []);
        } catch (error) {
          console.error("Error fetching promos:", error);
        }
      };
      fetchAvailablePromos();
    }, []);
    
        useEffect(() => {
          if (selectedTest ) {
            openBooking(selectedTest);
          }
        }, []);
    
     // ─── Promo helpers ─────────────────────────────────────────────────────────
      const applyPromoCode = async (code) => {
        const userId = sessionStorage.getItem("userId");
        const payload = {
          promo_code: code.toUpperCase(),
          service_type:"nursingAndPhysio",
          user_id: userId,
          order_price: bookingDlg.service?.price,
        };
        setPromoLoading(true);
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_APPLY_PROMOTION_API}/apply`,
            payload,
            { headers: { "Content-Type": "application/json" } }
          );
          const data = response.data;
          if (data.success) {
            justAppliedPromo.current = true;
            setAppliedPromo({ code: data.promo_code, discount: data.discount_amount, title: data.title,discount_perc: data.discount_perc });
            setPromoCode(""); setShowAvailablePromos(false);
            setForm({
              ...form,
              promo_code: { code: data.promo_code, discount: data.discount_amount, title: data.title,discount_perc: data.discount_perc }
            });
            setPromoMsg("Applied")
            setPromotype("success")
            setDialogMessage(data.message || "Promo code applied successfully!"); setDialogOpen(true);
          } else {
            setPromoMsg("Invalid")
            setPromotype("error")
            setDialogMessage(data.error || "Invalid promo code"); setDialogOpen(true);
          }
        } catch {
          setPromoMsg("Invalid")
          setDialogMessage("Failed to apply promo code. Please try again."); setDialogOpen(true);
        } finally {
          setPromoLoading(false);
        }
      };
      const removePromoCode = () => {
      setAppliedPromo(null); setDialogMessage("Promo code removed"); setDialogOpen(true);setPromoMsg("Removed!")
    };
  
  // ── Fetch services ───────────────────────────────────────────────────────────
  const fetchServices = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/nursingphysiotherapy/services`, { headers: getAuthHeader() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.success) setServices(d.services || []);
      else { setError(d.error || "Failed to load services"); notify(d.error || "Failed to load", "error"); }
    } catch (err) {
      console.error("fetchServices:", err);
      setError("Cannot reach server. Please try again.");
      notify("Cannot reach server", "error");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  // ── Booking flow ─────────────────────────────────────────────────────────────


const openBooking = async (svc) => {
  setForm(emptyForm());
  setStep(0);
  setSuccess(false);
  setMedicalDocs([]);
  setBookingDlg({ open: true, service: svc });

  try {
    const r = await fetch(`${API}/nursingphysiotherapy/bookings/generate-ref`, { headers: getAuthHeader() });
    const d = await r.json();
    if (d.success) setBookingRef(d.booking_ref);
  } catch {
    console.error("Failed to generate booking ref");
  }
};


const handleDocSelect = (e) => {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;
  setDocError("");

  if (medicalDocs.length + files.length > MAX_DOCS) {
    setDocError(`You can upload up to ${MAX_DOCS} files`);
    return;
  }

  const valid = [];
  for (const file of files) {
    if (!ALLOWED_DOC_TYPES.includes(file.type)) {
      setDocError("Only PDF, JPG or PNG files are allowed");
      return;
    }
    if (file.size > MAX_DOC_MB * 1024 * 1024) {
      setDocError(`Each file must be under ${MAX_DOC_MB}MB`);
      return;
    }
    valid.push(file);
  }
  setMedicalDocs(prev => [...prev, ...valid]);
  e.target.value = "";   
};

const removeDoc = (index) => {
  setMedicalDocs(prev => prev.filter((_, i) => i !== index));
  setDocError("");
};
const validateStep = () => {
  const current = STEPS[step];

  if (current === "Your Details") {
    if (!form.patient_name.trim())  { notify("Enter patient name", "warning"); return false; }
    if (!form.patient_phone.trim()) { notify("Enter phone number", "warning"); return false; }
    if (form.patient_phone.length < 10) { notify("Enter valid 10-digit number", "warning"); return false; }
    if (!form.patient_address.trim()) { notify("Enter home address", "warning"); return false; }
  }
  if (current === "Schedule") {
    if (!form.start_date) { notify("Select a start date", "warning"); return false; }
    if (!form.end_date)   { notify("Select an end date", "warning"); return false; }
    if (!form.scheduled_time) { notify("Select a time slot", "warning"); return false; }
  }

  return true;
};

const openDocPreview = (file) => {
  const url = URL.createObjectURL(file);
  setDocPreviewUrl(url);
  setDocPreviewIsPdf(file.type === "application/pdf");
};

const closeDocPreview = () => {
  if (docPreviewUrl) URL.revokeObjectURL(docPreviewUrl);
  setDocPreviewUrl("");
};
const uploadMedicalDocs = async () => {
  if (medicalDocs.length === 0) return [];
  setDocUploading(true);
  try {
    const urls = [];
    for (const file of medicalDocs) {
      const fd = new FormData();
      fd.append("document", file);
      fd.append("doc_type", "medical_record");
      // fd.append("user_id", getUserId() || "guest");
      fd.append("booking_ref", bookingRef);

      const r = await fetch(`${API}/nursingphysiotherapy/bookings/upload-document`, {
        method: "POST",
        headers: { ...getAuthHeader() },
        body: fd,
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || "Upload failed");
      urls.push(d.document_url);
    }
    return urls;
  } catch (err) {
    notify("Failed to upload documents. Please try again.", "error");
    throw err;
  } finally {
    setDocUploading(false);
  }
};


const submitBooking = async () => {
  if (!bookingDlg.service) return;
  const userId = getUserId();
  try {
    setBookingLoading(true);

  let document_urls = [];
  if (isPhysio) {
    document_urls = await uploadMedicalDocs();
  }

    const r = await fetch(`${API}/nursingphysiotherapy/bookings`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      body: JSON.stringify({
        ...form,
        service_id: bookingDlg.service._id,
        user_id: userId || "",
        document_urls,
        booking_ref: bookingRef,
      }),
    });
    const d = await r.json();
    if (d.success) setSuccess(true);
    else notify(d.error || "Booking failed", "error");
  } catch {
    notify("Booking failed. Try again.", "error");
  } finally {
    setBookingLoading(false);
  }
};

const closeBooking = () => {
  setBookingDlg({ open: false, service: null });
  setSuccess(false); setAppliedPromo(null); setPromoCode("");
  setMedicalDocs([]); setDocError(""); setBookingRef("");
};


  // ── Derived ──────────────────────────────────────────────────────────────────
  const CATS     = ["all", "nursing", "physiotherapy"];
  const filtered = services.filter((s) => tab === 0 || s.category === CATS[tab]);
  const nurCnt   = services.filter((s) => s.category === "nursing").length;
  const phyCnt   = services.filter((s) => s.category === "physiotherapy").length;

  return (
    <ThemeProvider theme={ayujaTheme}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* ── Outer wrapper — matches Header.jsx sidebar/AppBar offsets ── */}
      <Box sx={{
         minHeight: "100vh",
        ml: { xs: 0, md: 0 },
        pt: { xs: 0, md: 0 },
        width: { xs: "100%", md: "100%" },
        boxSizing: "border-box", overflowX: "hidden", pb: 6,
      }}>

        {/* ── Sticky page header ── */}
        <Box sx={{
          backgroundImage: `
                       linear-gradient(135deg, rgba(0,109,119,0.9) 0%, rgba(0, 169, 181, 0.61) 100%),
                       url(${bg2})
                     `,
                     backgroundSize: "cover",
                     backgroundPosition: "center",
                     backgroundRepeat: "no-repeat", borderBottom: "1px solid rgba(0,109,119,0.12)",
          px: { xs: 2, sm: 3, md: 4 }, py: { xs: 1.5, sm: 2 },
          position: "sticky", top: { xs: `${MOBILE_BAR_H}px`, md: 0 },
          zIndex: 99, boxShadow: "0 1px 8px rgba(0,109,119,0.06)",
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}
              >
              <IconButton onClick={() => navigate(-1)} sx={{ color: "#ffffff", p: { xs: 0.5, sm: 1 } }}>
                <ArrowBack sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />
              </IconButton>
              <Box>
              <Typography variant="h6" color="#ffffff" fontWeight={800} sx={{ fontSize: { xs: 15, sm: 19, md: 21 } }}>
                Nursing & Physiotherapy
              </Typography>
              <Typography variant="caption" color="#ffffff" sx={{ fontSize: { xs: 11, sm: 12 } }}>
                Professional home healthcare — book a certified specialist today
              </Typography>
            </Box>
            </Box>
            </Box>
            {/* ── Opens MyBookingsPage dialog ── */}
            <Button
              variant="outlined" startIcon={<HistoryIcon />}
              size={isMobile ? "small" : "medium"}
              onClick={() => setMyBkgOpen(true)}
              sx={{
                borderColor: "#ffffff", color:"#ffffff", whiteSpace: "nowrap",bgcolor:"#38a5b3a8",
                "&:hover": { bgcolor: alpha("#ffffffaf", 0.05), borderColor: "primary.dark" },
              }}
            >
              {isMobile ? "My Bkgs" : "My Bookings"}
            </Button>
          </Box>

          <Tabs
            value={tab} onChange={(_, v) => setTab(v)}
            variant={isMobile ? "fullWidth" : "standard"} scrollButtons={false}
            sx={{
              mt: 1,
              "& .MuiTabs-indicator": { backgroundColor: "#ffffff", height: 3, borderRadius: 2,borderColor:"#ffffff" },
              "& .Mui-selected": { color: "#ffffff !important" },
            }}
          >
            <Tab label="All Services"  sx={{ fontSize: { xs: 12, sm: 13 }, px: { xs: 1, sm: 2 } ,color:"#ffffff"}} />
            <Tab icon={<LocalHospitalIcon sx={{ fontSize: { xs: 14, sm: 16 },color:"#ffffff" }} />} iconPosition="start"
              label="Nursing" sx={{ fontSize: { xs: 12, sm: 13 }, px: { xs: 1, sm: 2 },color:"#ffffff" }} />
            <Tab icon={<FitnessCenterIcon sx={{ fontSize: { xs: 14, sm: 16 } ,color:"#ffffff"}} />} iconPosition="start"
              label="Physiotherapy" sx={{ fontSize: { xs: 12, sm: 13 }, px: { xs: 1, sm: 2 } ,color:"#ffffff"}} />
          </Tabs>
        </Box>

        {/* ── Hero banner ── */}
        <Box sx={{
          background: "#ffffff",
          px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2.5, sm: 3.5 },
          position: "relative", overflow: "hidden",
          mt:{xs:9,md:0},
          width: { xs: "100%", md:"100%" },
        }}>
          <Box sx={{ position: "absolute", right: -40, top: -40, width: 200, height: 200, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", right: 60, bottom: -60, width: 140, height: 140, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
          <Typography sx={{ color: "white", fontWeight: 800, fontSize: { xs: 16, sm: 20, md: 24 }, mb: 0.5, position: "relative",color:"black" }}>
            Healthcare at Your Doorstep 🏠
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: { xs: 12, sm: 13 }, mb: 2, position: "relative" ,color:"black" }}>
            Certified professionals · Scheduled visits · Transparent pricing
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ position: "relative" }}>
            <StatPill icon={<LocalHospitalIcon />} label={`${nurCnt} Nursing`} />
            <StatPill icon={<FitnessCenterIcon />} label={`${phyCnt} Physio`} />
            <StatPill icon={<VerifiedUserIcon />}  label="Certified Staff" />
            <StatPill icon={<StarIcon />}           label="Top Rated" />
          </Stack>
        </Box>

        {/* ── Content ── */}
        <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2.5, md: 3 }, pt: { xs: 3, sm: 4 } , bgColor:"#ffffff" }}>

          {loading && (
            <Box sx={{ textAlign: "center", py: 12 }}>
              <CircularProgress size={48} sx={{ color: "primary.main" }} />
              <Typography mt={2} color="text.secondary" fontSize={14} fontWeight={500}>Loading services…</Typography>
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Alert severity="error" sx={{ maxWidth: 480, mx: "auto", borderRadius: 2, mb: 2 }}>{error}</Alert>
              <Button variant="outlined" color="primary" onClick={fetchServices}>Retry</Button>
            </Box>
          )}

          {!loading && !error && filtered.length === 0 && (
            <Box sx={{ textAlign: "center", py: 12 }}>
              <MedicalServicesIcon sx={{ fontSize: 64, color: "primary.light", opacity: 0.3, mb: 2 }} />
              <Typography fontWeight={700} color="text.secondary" mb={0.5}>No services available</Typography>
              <Typography fontSize={13} color="text.secondary">
                {tab === 0 ? "No services added yet." : `No ${CATS[tab]} services currently available.`}
              </Typography>
            </Box>
          )}

          {/* ── CSS Grid for perfectly uniform card heights & widths ── */}
          {!loading && !error && filtered.length > 0 && (
            <Box sx={{
              display: "grid",
              gap: { xs: 2, sm: 2.5, md: 3 },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              alignItems: "stretch",
            }}>
              {filtered.map((svc) => {
                const isNursing = svc.category === "nursing";
                const accentClr = isNursing ? "#006D77" : "#b8654a";
                const accentBg  = isNursing ? alpha("#006D77", 0.07) : alpha("#E29578", 0.09);
                const badgeBg   = isNursing ? alpha("#006D77", 0.1)  : alpha("#E29578", 0.14);

                return (
                  <Card key={svc._id} sx={{
                    display: "flex", flexDirection: "column",
                    borderRadius: 3, overflow: "hidden",
                    border: `1px solid ${alpha(accentClr, 0.15)}`,
                  }}>
                    {/* Top accent stripe */}
                    <Box sx={{
                      height: 4,
                      background: isNursing
                        ? "linear-gradient(90deg, #006D77, #00949f)"
                        : "linear-gradient(90deg, #E29578, #c97a60)",
                    }} />

                    <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column" }}>
                      {/* Header row */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Avatar sx={{
                          bgcolor: accentBg, color: accentClr,
                          width: { xs: 44, sm: 48 }, height: { xs: 44, sm: 48 },
                          boxShadow: `0 4px 12px ${alpha(accentClr, 0.18)}`,
                        }}>
                          {isNursing ? <LocalHospitalIcon sx={{ fontSize: 22 }} /> : <FitnessCenterIcon sx={{ fontSize: 22 }} />}
                        </Avatar>
                        <Chip
                          label={isNursing ? "Nursing" : "Physiotherapy"}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          sx={{ bgcolor: badgeBg, color: accentClr, fontWeight: 700, fontSize: 10, border: `1px solid ${alpha(accentClr, 0.2)}`,pointerEvents: "none", }}
                        />
                      </Box>

                      <Typography variant="h6" sx={{ fontSize: { xs: 14, sm: 15 }, lineHeight: 1.35, mb: 1, fontWeight: 700 }}>
                        {svc.name}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{
                        mb: 1.5, lineHeight: 1.65, fontSize: { xs: 12, sm: 13 },
                        flexGrow: 1,
                        display: "-webkit-box", WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {svc.description || "Professional home healthcare service by certified specialists."}
                      </Typography>

                      {/* Condition tags */}
                      {svc.conditions_covered?.length > 0 && (
                        <Box sx={{ mb: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {svc.conditions_covered.slice(0, 3).map((c, i) => (
                            <Chip key={i} label={c} size="small" variant="outlined"
                              sx={{ fontSize: "0.62rem", borderColor: alpha(accentClr, 0.3), color: accentClr }} />
                          ))}
                          {svc.conditions_covered.length > 3 && (
                            <Chip label={`+${svc.conditions_covered.length - 3}`} size="small" sx={{ fontSize: "0.62rem" }} />
                          )}
                        </Box>
                      )}

                      <Divider sx={{ my: 1.5, borderColor: alpha(accentClr, 0.1) }} />

                      {/* Price & duration */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
                            <CurrencyRupeeIcon sx={{ fontSize: 15, color: "primary.main" }} />
                            <Typography fontWeight={800} color="primary.main" sx={{ fontSize: { xs: 16, sm: 18 } }}>
                              {svc.price}
                            </Typography>
                          </Box>
                          {/* {svc.price_b2b && (
                            <Typography variant="caption" color="text.secondary" fontSize={10}>
                              B2B: ₹{svc.price_b2b}
                            </Typography>
                          )} */}
                        </Box>
                        {svc.duration && (
                          <Box sx={{
                            display: "flex", alignItems: "center", gap: 0.5,
                            bgcolor: alpha(accentClr, 0.07), px: 1, py: 0.4, borderRadius: 10,
                          }}>
                            <AccessTimeIcon sx={{ fontSize: 12, color: accentClr }} />
                            <Typography variant="caption" fontWeight={600} sx={{ color: accentClr, fontSize: 11 }}>
                              {svc.duration}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>

                    {/* Book button — pinned to bottom */}
                    <CardActions sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 }, pt: 0 }}>
                      <Button
                        fullWidth variant="contained"
                        startIcon={<BookmarkAddIcon />}
                        onClick={() => openBooking(svc)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          py: 1, fontSize: { xs: 12, sm: 13 },
                          ...(isNursing
                            ? {}
                            : {
                                background: "linear-gradient(135deg, #E29578 0%, #c97a60 100%)",
                                color: "white",
                                boxShadow: "0 4px 14px rgba(226,149,120,0.32)",
                                "&:hover": { boxShadow: "0 6px 20px rgba(226,149,120,0.44)" },
                              }),
                        }}
                      >
                        Book Now
                      </Button>
                    </CardActions>
                  </Card>
                );
              })}
            </Box>
          )}
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════════
          BOOKING DIALOG
      ══════════════════════════════════════════════════════════════ */}
      <Dialog open={bookingDlg.open} onClose={closeBooking} maxWidth="sm" fullWidth
        fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, overflow: "hidden" } }}>
        <Box sx={{ height: 5, background: "linear-gradient(90deg, #006D77, #00949f)" }} />
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e8f4f5", pb: 1.5, pt: 2 }}>
          <Box>
            <Typography fontWeight={800} fontSize={{ xs: 15, sm: 18 }} color="primary.main">Book Service</Typography>
            {bookingDlg.service && (
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {bookingDlg.service.name} · ₹{bookingDlg.service.price}
              </Typography>
              
            )}
            <Divider></Divider>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Home Visint Charges· ₹{DELIVERY_CHARGES}
              </Typography>
          </Box>
          <IconButton size="small" onClick={closeBooking}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
          {!success ? (
            <>
              <Stepper activeStep={step} alternativeLabel={isMobile}
                sx={{ mb: 3.5, "& .MuiStepLabel-label": { fontSize: { xs: 10, sm: 12 }, fontWeight: 600 } }}>
                {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
              </Stepper>

              {/* Step 0 */}
              {/* {step === 0 && ( */}
              {STEPS[step] === "Your Details" && (
                <Stack spacing={2.5}>
                  <TextField label="Patient Full Name *" fullWidth value={form.patient_name}
                    onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 17, color: "primary.main" }} /></InputAdornment> }} />
                  <TextField label="Patient Age (Optional)" fullWidth value={form.age} type="number"
                    onChange={(e) => setForm({ ...form, age: e.target.value })}            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 17, color: "primary.main" }} /></InputAdornment> }} />
                  <TextField label="Phone Number *" fullWidth value={form.patient_phone}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) => setForm({ ...form, patient_phone: e.target.value.replace(/\D/g, "") })}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 17, color: "primary.main" }} /></InputAdornment> }}
                    helperText="10-digit mobile number" />
                  <TextField label="Home Address *" fullWidth multiline rows={3} value={form.patient_address}
                    onChange={(e) => setForm({ ...form, patient_address: e.target.value })}
                    placeholder="House no., street, area, city, pincode…"
                    InputProps={{ startAdornment: <InputAdornment position="start"><LocationOnIcon sx={{ fontSize: 17, color: "primary.main" }} /></InputAdornment> }} />
                </Stack>
              )}

              {/* Step 1 */}
              {/* {step === 1 && ( */}
              {STEPS[step] === "Schedule" && (
                <Stack spacing={2.5}>
                                   <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Start Date *"
                        type="date"
                        fullWidth
                        value={form.start_date}
                        onChange={(e) =>
                          setForm({ ...form, start_date: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarTodayIcon
                                sx={{ fontSize: 17, color: "primary.main" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="End Date *"
                        type="date"
                        fullWidth
                        value={form.end_date}
                        onChange={(e) =>
                          setForm({ ...form, end_date: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarTodayIcon
                                sx={{ fontSize: 17, color: "primary.main" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Box>
                    <Typography variant="body2" fontWeight={700} mb={1.5} color="text.secondary">Select Time Slot *</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {TIME_SLOTS.map((t) => (
                        <Chip key={t} label={t} clickable
                          color={form.scheduled_time === t ? "primary" : "default"}
                          variant={form.scheduled_time === t ? "filled" : "outlined"}
                          onClick={() => setForm({ ...form, scheduled_time: t })}
                          size={isMobile ? "small" : "medium"}
                          icon={<ScheduleIcon sx={{ fontSize: "13px !important" }} />}
                          sx={{ borderRadius: 2, fontWeight: 600, ...(form.scheduled_time !== t && { borderColor: alpha("#006D77", 0.3) }) }} />
                      ))}
                    </Box>
                  </Box>
                  <TextField label="Notes (optional)" fullWidth multiline rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Medical history, conditions, special requirements…" />
                </Stack>
              )}


            {STEPS[step] === "Medical Records" && (
              <Stack spacing={2.5}>
                <Alert severity="info" sx={{ borderRadius: 2, fontSize: 13 }}>
                   If you have any medical records or a signed treatment agreement, you can upload them here (optional).
                </Alert>

                <Box component="label" sx={{
                  border: "2px dashed", borderColor: alpha("#006D77", 0.35),
                  borderRadius: 3, py: 4, px: 3, textAlign: "center", cursor: "pointer", display: "block",
                  "&:hover": { bgcolor: alpha("#006D77", 0.04) },
                }}>
                  <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png" onChange={handleDocSelect} />
                  <MedicalServicesIcon sx={{ fontSize: 32, color: "primary.main", mb: 1 }} />
                  <Typography fontWeight={700} fontSize={14}>Click to upload medical records / agreements</Typography>
                  <Typography fontSize={12} color="text.secondary" mt={0.5}>
                    PDF, JPG or PNG — up to {MAX_DOC_MB}MB each, max {MAX_DOCS} files
                  </Typography>
                </Box>

                {medicalDocs.length > 0 && (
                  <Stack spacing={1}>
                    {medicalDocs.map((file, i) => (
                      <Paper key={i} variant="outlined" sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        p: 1.25, borderRadius: 2, borderColor: alpha("#006D77", 0.25),
                      }}>
                        <MedicalServicesIcon sx={{ color: "primary.main", fontSize: 20 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontSize={13} fontWeight={700} noWrap>{file.name}</Typography>
                          <Typography fontSize={11} color="text.secondary">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                        {/* <IconButton size="small" onClick={() => removeDoc(i)}><CloseIcon fontSize="small" /></IconButton>
                      </Paper> */}
                      <IconButton size="small" onClick={() => openDocPreview(file)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => removeDoc(i)}><CloseIcon fontSize="small" /></IconButton>
                      </Paper>
                    ))}
                  </Stack>
                )}

                {docError && <Alert severity="error" sx={{ borderRadius: 2, fontSize: 12 }}>{docError}</Alert>}
              </Stack>
            )}


              {/* Step 2 */}
              {/* {step === 2 && ( */}
              {STEPS[step] === "Review & Confirm" && (
                <Box>
                    <Box
                       sx={{
                         mb: 2,
                         p: 1.5,
                         borderRadius: 2,
                         bgcolor: "#FEF3C7",
                         border: "1px solid #FCD34D"
                       }}
                     >
                       <Typography
                         sx={{
                           fontSize: "13px",
                           fontWeight: 600,
                           color: "#92400E"
                         }}
                       >  🎁 Have a promo code? Apply it below to get discounts on your booking when you pay later. You can also view all available offers.
                       </Typography>
                     </Box>
                       {appliedPromo && (
                         <div style={styles.summaryRow}>
                           <span style={{ ...styles.summaryLabel, color: "#d97706", fontWeight: "600" }}>🏷️ Promo ({appliedPromo.code})</span>
                           {appliedPromo.discount_perc == 0 &&(<span style={{ ...styles.summaryValue, color: "#d97706" }}> -₹{appliedPromo.discount.toFixed(2)}</span>)}
                            {appliedPromo.discount_perc > 0 &&(<span style={{ ...styles.summaryValue, color: "#d97706" }}>-{appliedPromo.discount_perc}%</span>)}
                         </div>
                       )}
                  
                       <div style={styles.divider} />
                  
                       
                       {/* Promo section */}
                       {!appliedPromo ? (
                         <>
                           <div style={styles.promoInput}>
                             <input type="text" placeholder="Enter promo code" value={promoCode}
                               onChange={(e) => setPromoCode(e.target.value.toUpperCase())} style={styles.input} />
                             <button onClick={() => applyPromoCode(promoCode)} disabled={!promoCode || promoLoading}
                               style={{ ...styles.button, ...styles.primaryButton, opacity: !promoCode ? 0.5 : 1 }}>
                               {promoLoading ? "Applying..." : "Apply"}
                             </button>
                           </div>
                           <button onClick={() => setShowAvailablePromos(!showAvailablePromos)}
                             style={{ ...styles.button, ...styles.secondaryButton, width: "100%", marginTop: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                             <Tag size={16} /> View Available Offers
                             {showAvailablePromos ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                           </button>
                           {showAvailablePromos && availablePromos.length > 0 && (
                             <div style={{ marginTop: "12px" }}>
                               {availablePromos.map((promo, index) => (
                                 <div key={index} style={styles.promoCard}
                                   onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#006D77"; e.currentTarget.style.backgroundColor = "#F0FDFA"; }}
                                   onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.backgroundColor = "#fff"; }}
                                   onClick={() => applyPromoCode(promo.promo_code)}>
                                   <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                                     <div>
                                       <div style={{ fontSize: "16px", fontWeight: "700", color: "#111827", marginBottom: "4px" }}>{promo.promo_code}</div>
                                       <div style={{ fontSize: "14px", color: "#6B7280", marginBottom: "8px" }}>{promo.description}</div>
                                       <div style={{ fontSize: "12px", color: "#9CA3AF" }}>
                                         Min order: ₹{promo.min_order_value}{promo.max_discount > 0 && ` • Max discount: ₹${promo.max_discount}`}
                                       </div>
                                     </div>
                                     <button style={{ ...styles.button, ...styles.primaryButton, fontSize: "12px", padding: "6px 12px" }}>Apply</button>
                                   </div>
                                 </div>
                               ))}
                             </div>
                           )}
                         </>
                       ) : (
                         <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                           <div style={styles.promoTag}><Tag size={16} />{appliedPromo.code} Applied</div>
                           <button onClick={removePromoCode} style={{ ...styles.button, ...styles.dangerButton, fontSize: "12px", padding: "6px 12px" }}>Remove</button>
                         </div>
                       )}
                                              
                  <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>Review your details carefully before confirming.</Alert>
                  <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden", border: "1px solid rgba(0,109,119,0.15)" }}>
                    <Box sx={{ background: "linear-gradient(135deg, #006D77, #00949f)", px: 2.5, py: 1.8 }}>
                      <Typography fontWeight={800} color="white" fontSize={{ xs: 13, sm: 15 }}>{bookingDlg.service?.name}</Typography>
                      <Typography variant="caption" sx={{ color: alpha("#fff", 0.8) }}>
                        {bookingDlg.service?.category} · ₹{bookingDlg.service?.price}
                      </Typography>
                    </Box>
                    {[
                      [<PersonIcon />,         "Patient",   form.patient_name],
                      [<PhoneIcon />,           "Phone",     form.patient_phone],
                      [<PersonIcon />,          "Age",        form.age],
                      [<LocationOnIcon />,      "Address",   form.patient_address],
                      [<EventIcon />,          "Start Date",      form.start_date],
                      [<EventIcon />,           "End Date",      form.end_date],       
                      [<ScheduleIcon />,        "Time",      form.scheduled_time],
                      [<MedicalServicesIcon />, "Notes",     form.notes || "—"],
                    ].map(([icon, label, value], i) => (
                      <Box key={i} sx={{
                        display: "flex", alignItems: "flex-start", gap: 1.5,
                        px: 2.5, py: 1.2,
                        bgcolor: i % 2 === 0 ? "white" : alpha("#006D77", 0.025),
                        borderBottom: i < 5 ? "1px solid #f0f7f8" : "none",
                      }}>
                        <Box sx={{ color: "primary.main", mt: 0.3, "& svg": { fontSize: 15 } }}>{icon}</Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}
                            textTransform="uppercase" letterSpacing={0.6} display="block" fontSize={9.5}>{label}</Typography>
                          <Typography variant="body2" fontWeight={600} fontSize={{ xs: 12, sm: 13 }}>{value}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                  <FormControlLabel style={{paddingBottom:16,display: "flex", justifyContent: "center", alignItems: "center",}}
                                
                    control={
                      <Checkbox
                        size="small"
                        checked={acceptedTerms || false}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Accept Ayuja's{" "}
                        <a href="/terms&conditions" target="_blank" rel="noopener noreferrer"
                          style={{ color: "#007a85", fontWeight: "bold", textDecoration: "none" }}>
                          Terms and Conditions
                        </a>
                          <span style={{ color: "red", marginLeft: 2 ,fontSize:19}}>*</span>
                      </Typography>
                    }
                  />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: "center", py: { xs: 3, sm: 5 } }}>
              <Box sx={{
                width: 90, height: 90, borderRadius: "50%",
                bgcolor: alpha("#16a34a", 0.1),
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 2.5,
              }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 52, color: "success.main" }} />
              </Box>
              <Typography variant="h5" fontWeight={800} gutterBottom fontSize={{ xs: 18, sm: 22 }} color="primary.main">
                Booking Confirmed! 🎉
              </Typography>
              <Typography color="text.secondary" mb={2.5} fontSize={{ xs: 13, sm: 14 }} lineHeight={1.6}>
                Your booking for <strong>{bookingDlg.service?.name}</strong> has been received.
                Our team will contact you shortly to confirm the schedule.
              </Typography>
              <Alert severity="success" icon={<TagIcon />} sx={{ textAlign: "left", borderRadius: 2 }}>
                Track your booking via <strong>"Order - Nursing & Physiotherapy"</strong> — linked to your account automatically.
              </Alert>
            </Box>
          )}
        </DialogContent>

        {!success ? (
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1, borderTop: "1px solid #e8f4f5" }}>
            {step > 0 && <Button onClick={() => setStep(step - 1)} color="inherit" startIcon={<ArrowBackIcon />}>Back</Button>}
            <Box sx={{ flex: 1 }} />
            {/* {step < 2 */}
            {step < STEPS.length - 1
              ? <Button variant="contained" onClick={() => { if (validateStep()) setStep(step + 1); }} sx={{ px: 3 }}>Next →</Button>
              : <Button
                    variant="contained"
                    color="success"
                    onClick={submitBooking}
                    disabled={bookingLoading || !acceptedTerms}
                    sx={{ px: 3, minWidth: 170 }}
                  >
                    {bookingLoading ? (
                      <>
                        <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                        Booking...
                      </>
                    ) : (
                      <>
                        <CheckCircleOutlineIcon sx={{ mr: 1 }} />
                        Confirm Booking
                      </>
                    )}
                  </Button>}
          </DialogActions>
        ) : (
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: "1px solid #e8f4f5" }}>
            <Button variant="contained" fullWidth onClick={closeBooking} size="large">Done</Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════
          MY BOOKINGS — rendered via separate MyBookingsPage component
      ══════════════════════════════════════════════════════════════ */}
      <MyNurPhyBookingsPage
        open={myBkgOpen}
        onClose={() => setMyBkgOpen(false)}
      />


<Dialog open={!!docPreviewUrl} onClose={closeDocPreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography fontWeight={700}>Document Preview</Typography>
          <IconButton onClick={closeDocPreview} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#000", minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {docPreviewUrl && (
            docPreviewIsPdf ? (
              <iframe src={docPreviewUrl} title="document" width="100%" height="700px" style={{ border: "none" }} />
            ) : (
              <Box component="img" src={docPreviewUrl} alt="document" sx={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />
            )
          )}
        </DialogContent>
      </Dialog>
      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}